/*
 * The message dialect, built, the kit over it and the nine formatting actions over that kit — the
 * one instance the composer and everything around it work with. It exists so that the dialect is
 * assembled in exactly one place: a second schema or a second tokenizer over the same configuration
 * would be a second document model, and two ProseMirror schemas are never equal to each other
 * whatever they were built from.
 *
 * The shared parts of this directory never import this file: each takes a kit, and the composer
 * hands them this one. A surface that edits something other than a message builds its own kit
 * over its own schema and role map and imports nothing from here.
 */

import type { Node } from "prosemirror-model"
import { buildActionSet } from "./actions.ts"
import { buildDialect } from "./dialect/index.ts"
import { createEditorKit, type KitRoles } from "./kit.ts"
import { messageDialectConfig } from "./markdown/index.ts"

export const messageDialect = buildDialect(messageDialectConfig)

/**
 * The message's names for the roles a gesture asks for. A message has no check list of its own: a
 * box is an item's (`list_item{checked}`) inside a bullet list, so the check role names the bullet
 * list, and `KitRoles.lists`' order keeps every bullet list a bullet list.
 */
export const messageRoles: KitRoles = {
	paragraph: "paragraph",
	codeBlock: "code_block",
	blockquote: "blockquote",
	listItem: "list_item",
	hardBreak: "hard_break",
	heading: { node: "heading", levelAttr: "level" },
	lists: {
		bullet: { node: "bullet_list", attrs: {} },
		ordered: { node: "ordered_list", attrs: {} },
		check: { node: "bullet_list", attrs: {} },
	},
	table: { table: "table", cell: "table_cell" },
	image: { node: "image", text: ["alt", "src"] },
	marks: { bold: "strong", italic: "em", strike: "strike", code: "code", link: "link" },
	attrs: { orderedStart: "order", codeLanguage: "params", checked: "checked" },
}

export const messageKit = createEditorKit(messageDialect, messageRoles)

/** The nine formatting actions over the message kit, and the three steps of its link dialog. */
export const messageActions = buildActionSet(messageKit)

export const { schema } = messageDialect

/** The parser itself, for the mapping fixtures, which read the tokens behind the document. */
export const markdownParser = messageDialect.parser

/** The document one message describes. */
export function parseMarkdown(src: string): Node {
	return messageDialect.parse(src)
}

/** The markdown one message document stands for. This is what a draft holds and what is sent. */
export function serializeMarkdown(doc: Node): string {
	return messageDialect.serialize(doc)
}
